"use client";

import { useState, useRef, useEffect } from "react";
import { X, ImagePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useBlogStore } from "@/store/blogStore";
import { useBlogCategoryStore } from "@/store/blogCategoryStore";
import { useLanguage } from "@/context/LanguageContext";
import {
  getImageUploadCopy,
  prepareImageUpload,
} from "@/lib/image-upload";
import { isValidVideoUrl } from "@/utils";
import {
  ValidationFooter,
  ERROR_BORDER_CLASS,
  CrisisModal,
  InappropriateLanguageModal,
  InappropriateLanguageBanner,
  BlogCategoryMultiSelect,
  MAX_BLOG_CATEGORIES,
} from "@/components/ui";
import { useContentModeration } from "@/hooks/useContentModeration";

interface CreateBlogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (success: boolean, message: string) => void;
}

const MAX_TITLE_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 200;
const MAX_CONTENT_LENGTH = 50000;

export default function CreateBlogModal({ isOpen, onClose, onResult }: CreateBlogModalProps) {
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();
  const { createBlog, loading } = useBlogStore();
  const { categories, fetchCategories, hasFetched: categoriesLoaded } = useBlogCategoryStore();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [categoryDocumentIds, setCategoryDocumentIds] = useState<string[]>([]);
  const [featured, setFeatured] = useState(false);
  const [showFeaturedConfirm, setShowFeaturedConfirm] = useState(false);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [coverImageName, setCoverImageName] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadCopy = getImageUploadCopy(language, 'single');

  const handleFeaturedChange = (checked: boolean) => {
    setFeatured(checked);
    if (checked) {
      setShowFeaturedConfirm(true);
    }
  };

  useEffect(() => {
    if (isOpen && !categoriesLoaded) {
      fetchCategories();
    }
  }, [isOpen, categoriesLoaded, fetchCategories]);

  const handleCategoryToggle = (documentId: string) => {
    setCategoryDocumentIds((current) => {
      if (current.includes(documentId)) {
        return current.filter((id) => id !== documentId);
      }
      if (current.length >= MAX_BLOG_CATEGORIES) return current;
      return [...current, documentId];
    });
  };

  // Content moderation (MWBI-871). Scan the human-text fields only — title +
  // description + content — joined with "\n". The URL field (youtubeUrl) is
  // intentionally NOT scanned. The "\n" join stops a banned word from forming
  // across a field boundary: the inappropriate detector's \b…\b regex treats
  // "\n" as a non-word char, so it can't match across the seam. The crisis
  // detector uses a plain substring includes() (see utils/crisis.ts), so the
  // join only shields it insofar as no crisis keyword itself contains a "\n" —
  // which holds for every default keyword. Called above the isOpen
  // early-return to preserve the Rules of Hooks; the scanned text is gated on
  // isOpen so detection stays inert while the modal is mounted-but-closed
  // (CreateButton keeps this component mounted via the isOpen prop).
  const moderationText = isOpen ? [title, description, content].join('\n') : '';
  const {
    hasInappropriate,
    showCrisisModal,
    showInappropriateModal,
    dismissCrisisModal,
    dismissInappropriateModal,
  } = useContentModeration(moderationText);

  // Reset produced modal state when the surface goes inactive. Gating the input
  // (above) stops new detection, but any modal already opened survives in the
  // still-mounted hook. Crisis is informational and does NOT block submit, so a
  // user can publish with the advisory still open; resetForm() then clears the
  // fields while showCrisisModal stays true, and on reopen the advisory would
  // resurface over the clean form (reachable via keyboard/AT past the overlay).
  // Dismissing both modals on close closes that gap. CreateNewsModal and the
  // MWBI-869 Create Event surface share this latent issue (foundation fix:
  // MWBI-877/878).
  //
  // MWBI-770: the selected categories are cleared here too. Same reasoning —
  // this modal stays mounted via `isOpen` (CreateButton renders it, not a
  // conditional {open && <Modal/>}), so a selection made before the user
  // closes without submitting would otherwise silently resurface on reopen.
  useEffect(() => {
    if (!isOpen) {
      dismissCrisisModal();
      dismissInappropriateModal();
      setCategoryDocumentIds([]);
    }
  }, [isOpen, dismissCrisisModal, dismissInappropriateModal]);

  if (!isOpen) return null;

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const { file: prepared, error } = await prepareImageUpload(file, language);
      if (error) {
        onResult?.(false, error);
        return;
      }
      setCoverImage(prepared);
      setCoverImageName(prepared.name);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(prepared);
    }
  };

  const removeImage = () => {
    setCoverImage(null);
    setImagePreview(null);
    setCoverImageName(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const calculateReadTime = (text: string): number => {
    const wordsPerMinute = 200;
    const words = text.trim().split(/\s+/).length;
    return Math.max(1, Math.ceil(words / wordsPerMinute));
  };

  const handleSubmit = async (asDraft = false) => {
    // hasInappropriate gates BOTH paths (draft + publish): profanity must not go
    // through even as a draft. Defense-in-depth backstop for the disabled
    // buttons (isValid below) — keyboard/AT/programmatic callers must hit it too.
    if (!currentUser || !title.trim() || !description.trim() || !content.trim() || isSubmitting || hasInappropriate) return;
    if (categoryDocumentIds.length === 0 || categoryDocumentIds.length > MAX_BLOG_CATEGORIES) {
      onResult?.(false, t.modals.blog.errorOccurred);
      return;
    }

    const trimmedYoutubeUrl = youtubeUrl.trim();
    if (trimmedYoutubeUrl && !isValidVideoUrl(trimmedYoutubeUrl)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const success = await createBlog({
        title: title.trim(),
        description: description.trim(),
        content: content.trim(),
        youtubeUrl: trimmedYoutubeUrl || undefined,
        categoryDocumentIds,
        coverImage: coverImage || undefined,
        readTime: calculateReadTime(content),
        featured: asDraft ? false : featured,
        isDraft: asDraft,
      });

      if (success) {
        onResult?.(true, asDraft ? t.modals.blog.draftSavedSuccess : t.modals.blog.blogCreatedSuccess);
        resetForm();
        onClose();
      } else {
        // Read error directly from the store — `error` in the destructured hook
        // value is stale here because React hasn't re-rendered since createBlog()
        // set it. getState() returns the current value set by the failed request.
        const latestError = useBlogStore.getState().error;
        onResult?.(false, latestError || t.modals.blog.failedToCreate);
      }
    } catch (error) {
      console.error('src/components/ui/CreateBlogModal.tsx', error);
      onResult?.(false, t.modals.blog.errorOccurred);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setContent("");
    setYoutubeUrl("");
    setCategoryDocumentIds([]);
    setFeatured(false);
    setCoverImage(null);
    setImagePreview(null);
    setCoverImageName(null);
  };

  const isValid =
    title.trim().length > 0 &&
    title.length <= MAX_TITLE_LENGTH &&
    description.trim().length > 0 &&
    description.length <= MAX_DESCRIPTION_LENGTH &&
    content.trim().length > 0 &&
    content.length <= MAX_CONTENT_LENGTH &&
    (!youtubeUrl.trim() || isValidVideoUrl(youtubeUrl.trim())) &&
    categoryDocumentIds.length >= 1 &&
    categoryDocumentIds.length <= MAX_BLOG_CATEGORIES &&
    !hasInappropriate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="create-blog-root">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <div>
            <h2 className="text-lg font-semibold text-foreground" data-testid="create-blog-heading">{t.modals.blog.createBlogPost}</h2>
            <p className="text-sm text-caption">{t.modals.blog.writeArticle}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-sidebar-hover rounded transition" data-testid="create-blog-close-btn">
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <CrisisModal
            open={showCrisisModal}
            onClose={dismissCrisisModal}
            testIdPrefix="create-blog-crisis"
          />

          <InappropriateLanguageModal
            open={showInappropriateModal}
            onClose={dismissInappropriateModal}
            testIdPrefix="create-blog-inappropriate"
          />

          {hasInappropriate && (
            <InappropriateLanguageBanner testIdPrefix="create-blog-inappropriate" />
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t.modals.blog.title} *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.modals.blog.enterBlogTitle}
              className={`w-full px-3 py-2 border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary ${title.length > MAX_TITLE_LENGTH ? ERROR_BORDER_CLASS : 'border-border'}`}
              data-testid="create-blog-title-input"
            />
            <ValidationFooter
              value={title}
              maxLength={MAX_TITLE_LENGTH}
              tooLongMessage={t.errors.blogTitleTooLong}
              testIdPrefix="create-blog-title"
            />
          </div>

          <BlogCategoryMultiSelect
            selectedDocumentIds={categoryDocumentIds}
            onToggle={handleCategoryToggle}
            testIdPrefix="create-blog-category"
            disabled={categories.length === 0}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t.modals.blog.description} *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.modals.blog.briefSummary}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none ${description.length > MAX_DESCRIPTION_LENGTH ? ERROR_BORDER_CLASS : 'border-border'}`}
              data-testid="create-blog-description-textarea"
            />
            <ValidationFooter
              value={description}
              maxLength={MAX_DESCRIPTION_LENGTH}
              tooLongMessage={t.errors.blogDescriptionTooLong}
              testIdPrefix="create-blog-description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t.modals.blog.content} *</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t.modals.blog.writeContent}
              rows={12}
              className={`w-full px-3 py-2 border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none ${content.length > MAX_CONTENT_LENGTH ? ERROR_BORDER_CLASS : 'border-border'}`}
              data-testid="create-blog-content-textarea"
            />
            <ValidationFooter
              value={content}
              maxLength={MAX_CONTENT_LENGTH}
              tooLongMessage={t.errors.blogContentTooLong}
              testIdPrefix="create-blog-content"
              trailing={
                <span className="text-xs text-caption">
                  ~{calculateReadTime(content)} {t.modals.blog.minRead}
                </span>
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t.modals.blog.youtubeUrl}</label>
            <input
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder={t.modals.blog.youtubeUrlPlaceholder}
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="create-blog-youtube-url"
            />
            <p className="text-xs text-caption mt-1">{t.modals.blog.youtubeUrlHelp}</p>
            {youtubeUrl.trim() && !isValidVideoUrl(youtubeUrl.trim()) ? (
              <p className="text-sm text-red-600 mt-1" data-testid="create-blog-invalid-youtube-msg">{t.modals.blog.invalidYoutubeUrl}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{t.modals.blog.coverImage}</label>
            {imagePreview ? (
              <div>
                <div className="relative aspect-video rounded-md overflow-hidden border border-border">
                  <img src={imagePreview} alt="Cover preview" className="w-full h-full object-contain" />
                  <button
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                    data-testid="create-blog-cover-image-remove-btn"
                  >
                    <X size={16} />
                  </button>
                </div>
                {coverImageName && <p className="text-xs text-caption mt-1 truncate">{coverImageName}</p>}
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-md p-8 text-center cursor-pointer hover:border-primary/50 transition"
                data-testid="create-blog-cover-image-dropzone"
              >
                <ImagePlus className="mx-auto mb-2 text-foreground-muted" size={32} />
                <p className="text-sm text-foreground-muted">{uploadCopy.uploadPrompt}</p>
                <p className="text-xs text-caption mt-1">{uploadCopy.fileTypes}</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleImageChange}
              className="hidden"
              data-testid="create-blog-cover-image-file-input"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="featured"
              checked={featured}
              onChange={(e) => handleFeaturedChange(e.target.checked)}
              className="w-4 h-4 rounded border-border"
              data-testid="create-blog-featured-checkbox"
            />
            <label htmlFor="featured" className="text-sm text-foreground">
              {t.modals.blog.markAsFeatured}
            </label>
          </div>
          <p className="text-xs text-caption -mt-2">
            {t.modals.blog.featuredDescription}
          </p>
        </div>

        {showFeaturedConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="create-blog-featured-confirm-modal">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowFeaturedConfirm(false)} />
            <div className="relative bg-background rounded-lg shadow-xl p-6 max-w-sm mx-4">
              <p className="text-foreground text-center mb-4">
                {t.modals.blog.nowFeatured}
              </p>
              <button
                onClick={() => setShowFeaturedConfirm(false)}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition"
                data-testid="create-blog-featured-confirm-close-btn"
              >
                {t.common.close}
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-background">
          <button
            onClick={onClose}
            disabled={isSubmitting || loading}
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
            data-testid="create-blog-cancel-btn"
          >
            {t.common.cancel}
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={!isValid || isSubmitting || loading}
            className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-sidebar-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-blog-save-draft-btn"
          >
            {isSubmitting || loading ? t.modals.blog.saving : t.modals.blog.saveAsDraft}
          </button>
          <button
            onClick={() => handleSubmit(false)}
            disabled={!isValid || isSubmitting || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-blog-publish-btn"
          >
            {isSubmitting || loading ? t.modals.blog.creating : t.modals.blog.publishBlog}
          </button>
        </div>
      </div>
    </div>
  );
}

